/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";
const G = "#0077B5";
const GRN = "#1D9E75";
const CARD = "#FFFFFF";
const B = "#E1E9EE";

const SectionLabel = ({ children }) => (
  <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, letterSpacing: "0.2em", color: "#999999", textTransform: "uppercase", marginBottom: 16 }}>{children}</div>
);
const Divider = () => <div style={{ height: 1, background: B, margin: "28px 0" }} />;

export default function ScheduleTab({ user, setUser, dashState }) {
  const [postingMode, setPostingMode] = useState(user?.posting_mode || "review");
  const [reviewWindowDay, setReviewWindowDay] = useState(user?.review_window_day || "Sunday");
  const [reviewWindowTime, setReviewWindowTime] = useState(user?.review_window_time || "9:00am");
  const [reviewWindowTimezone, setReviewWindowTimezone] = useState(user?.review_window_timezone || "America/New_York");
  const [reviewWindowHours, setReviewWindowHours] = useState(user?.review_window_hours || 120);
  const [reviewEmailNotify, setReviewEmailNotify] = useState(user?.review_email_notify !== false);
  const [reviewWindowSaving, setReviewWindowSaving] = useState(false);
  const [reviewWindowSaved, setReviewWindowSaved] = useState(false);
  const [postsPerWeekOverride, setPostsPerWeekOverride] = useState(user?.posts_per_week_override ?? null);
  const [isPaused, setIsPaused] = useState(!!user?.is_paused);
  const [postCountSaving, setPostCountSaving] = useState(false);
  const [postCountSaved, setPostCountSaved] = useState(false);
  const [weekendPosting, setWeekendPosting] = useState(!!user?.weekend_posting);

  const plan = (user?.plan || "presence").toLowerCase();
  const TIER_SLOTS = { free: 2, presence: 4, authority: 5, team: 5, agency: 5 };
  const maxSlots = TIER_SLOTS[plan] || 4;
  const effectiveSlots = postsPerWeekOverride !== null ? postsPerWeekOverride : maxSlots;

  const saveSetting = async (key, val) => {
    await fetch(`${API_BASE}/app/api/user/settings`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ [key]: val }) });
    setUser(u => u ? { ...u, [key]: val } : u);
  };

  const handlePostingMode = async (mode) => {
    setPostingMode(mode);
    await fetch(`${API_BASE}/app/api/user/posting-mode`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mode }) });
    setUser(u => u ? { ...u, posting_mode: mode } : u);
  };

  const saveReviewWindow = async () => {
    setReviewWindowSaving(true);
    try {
      await fetch(`${API_BASE}/app/api/user/settings`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ review_window_day: reviewWindowDay, review_window_time: reviewWindowTime, review_window_timezone: reviewWindowTimezone, review_email_notify: reviewEmailNotify }) });
      setUser(u => u ? { ...u, review_window_day: reviewWindowDay, review_window_time: reviewWindowTime, review_window_timezone: reviewWindowTimezone, review_email_notify: reviewEmailNotify } : u);
      setReviewWindowSaved(true);
      setTimeout(() => setReviewWindowSaved(false), 3000);
    } catch {}
    setReviewWindowSaving(false);
  };

  const handlePostCount = async (count) => {
    setPostsPerWeekOverride(count);
    setPostCountSaving(true);
    const isPausedNow = count === 0;
    setIsPaused(isPausedNow);
    try {
      await fetch(`${API_BASE}/app/api/user/settings`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ posts_per_week_override: count === maxSlots ? null : count, is_paused: isPausedNow }) });
      setUser(u => u ? { ...u, posts_per_week_override: count === maxSlots ? null : count, is_paused: isPausedNow } : u);
      setPostCountSaved(true);
      setTimeout(() => setPostCountSaved(false), 3000);
    } catch {}
    setPostCountSaving(false);
  };

  return (
    <div>
      <SectionLabel>Posting mode</SectionLabel>
      <div style={{ display: "flex", gap: 12, marginBottom: 28 }} className="settings-mode-grid">
        {[
          { id: "review", label: "Edit First", desc: "Posts are generated and you get a window to edit, swap, or remove before they go out.", icon: "📋" },
          { id: "autopilot", label: "Autopilot", desc: "Posts are generated and go out automatically — no action needed.", icon: "🤖" },
        ].map(m => (
          <div key={m.id} onClick={() => handlePostingMode(m.id)}
            style={{ flex: 1, border: `1px solid ${postingMode === m.id ? G : B}`, borderRadius: 10, padding: "16px", cursor: "pointer", background: postingMode === m.id ? "#EBF5FB" : "transparent", transition: "all 0.2s" }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>{m.icon}</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666", lineHeight: 1.5 }}>{m.desc}</div>
          </div>
        ))}
      </div>

      {postingMode === "review" && (
        <>
          <Divider />
          <SectionLabel>Edit window email</SectionLabel>
          <div style={{ fontSize: 14, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 16, lineHeight: 1.6 }}>
            Posts go out at their scheduled time. You get an email with a window to edit, swap, or remove before they publish.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666", marginBottom: 6 }}>Email day</div>
              <select value={reviewWindowDay} onChange={e => setReviewWindowDay(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${B}`, fontSize: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                {["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <label style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: "#666", display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={reviewEmailNotify} onChange={e => setReviewEmailNotify(e.target.checked)} />
              Email me when posts are ready to edit
            </label>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={saveReviewWindow} disabled={reviewWindowSaving} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 13, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", opacity: reviewWindowSaving ? 0.6 : 1 }}>
              {reviewWindowSaving ? "Saving..." : "Save"}
            </button>
            {reviewWindowSaved && <span style={{ fontSize: 13, color: GRN }}>✓ Saved</span>}
          </div>
        </>
      )}

      <Divider />
      <SectionLabel>Posts per week</SectionLabel>
      {plan === "free" ? (
        <div style={{ fontSize: 14, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 16, lineHeight: 1.6 }}>
          Your free plan includes 2 posts per week — BrandHives content adapted to your voice. Upgrade to adjust your posting frequency.
        </div>
      ) : (
      <>
      <div style={{ fontSize: 14, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 16, lineHeight: 1.6 }}>
        Your plan includes up to {maxSlots} posts/week. Slide to adjust or set to 0 to pause.
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
        <input type="range" min={0} max={maxSlots} value={effectiveSlots} onChange={e => handlePostCount(Number(e.target.value))} style={{ flex: 1 }} />
        <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 20, fontWeight: 600, minWidth: 40, textAlign: "center", color: effectiveSlots === 0 ? "#D85A30" : "#1A1A1A" }}>
          {effectiveSlots === 0 ? "⏸" : effectiveSlots}
        </div>
      </div>
      {isPaused && (
        <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: "#92400E" }}>
          Posting is paused. Slide above 0 to resume.
        </div>
      )}
      {postCountSaved && <div style={{ fontSize: 13, color: GRN, marginBottom: 16 }}>✓ Saved</div>}
      </>
      )}

      <Divider />
      <SectionLabel>Writing voice</SectionLabel>
      <div style={{ fontSize: 14, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 16, lineHeight: 1.6 }}>
        How should your posts be written?
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 28 }} className="settings-mode-grid">
        {[
          { id: "about_company", label: "Me personally", desc: "Posts use 'I', 'my', 'I've found' — your personal professional voice.", icon: "\uD83D\uDC64" },
          { id: "as_company", label: "My company", desc: "Posts use 'we', 'our', 'our team' — speak as your company.", icon: "\uD83C\uDFE2" },
        ].map(m => (
          <div key={m.id} onClick={() => saveSetting("voice_direction", m.id)}
            style={{ flex: 1, border: `1px solid ${(user?.voice_direction || "about_company") === m.id ? G : B}`, borderRadius: 10, padding: "16px", cursor: "pointer", background: (user?.voice_direction || "about_company") === m.id ? "#EBF5FB" : "transparent", transition: "all 0.2s" }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>{m.icon}</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666", lineHeight: 1.5 }}>{m.desc}</div>
          </div>
        ))}
      </div>

      <Divider />
      <SectionLabel>Link style</SectionLabel>
      <div style={{ marginTop: 20, padding: 16, background: "#F9FAFB", borderRadius: 8, border: "1px solid #E5E7EB" }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Link handling</div>
        <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
          Article links are embedded in your post preview. First-comment posting is coming in a future update.
        </div>
      </div>

      {["presence","authority","team","agency"].includes(plan) && (
        <>
          <Divider />
          <SectionLabel>Weekend posting</SectionLabel>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "14px 18px" }}>
            <div>
              <div style={{ fontSize: 15, marginBottom: 4 }}>Post on Saturdays</div>
              <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666" }}>Adds Saturday as a posting day (Sunday never used).</div>
            </div>
            <button onClick={() => { const v = !weekendPosting; setWeekendPosting(v); saveSetting("weekend_posting", v); }}
              style={{ background: weekendPosting ? GRN : "#E0E0E0", color: weekendPosting ? "#fff" : "#666", border: "none", borderRadius: 20, padding: "6px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: "pointer", minWidth: 56, transition: "all 0.2s" }}>
              {weekendPosting ? "On" : "Off"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
